import React, { useState } from 'react';

// API/Components
import { 
	ANALYSIS_RESULT,
	ANALYSIS_TYPE,
	STATE_COLOR,
	AnalysisResultToStateColor,
	UnitAnalysisToStateText,
	parseVoltageAnalysisResult,
	parseImpedanceAnalysisResult,
	isEmpty,
	buildMeasurementText,
	formatDate
} from './enums';

// Material UI
import Backdrop from '@mui/material/Backdrop';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InLimitsIcon from '@mui/icons-material/CheckCircleOutline';
import WarrantyIcon from '@mui/icons-material/Info';
import AllUnitsIcon from '@mui/icons-material/ViewList';

// Within Limits text constants
const allUnitsWithinLimitsText = 'All units are within limits';
const unitWithinLimitsText = 'This unit is within limits';

// Table column widths
const unitNumColumnWidth = '50px';
const installDateColumnWidth = '100px';
const measurementColumnWidth = '175px';

export default function UnitAnalysisSummary({ unitAnalysisData, loading = false }) {

	// Display all individual unit analyses, including those within limits
	const [displayAllAnalyses, setDisplayAllAnalyses] = useState(false);

	// Display all units, even if they are within limits
	const [displayAllUnits, setDisplayAllUnits] = useState(false);

	// Display warranty information (if displayAllUnits is not checked, only display
	//  warranty information for units that are out of warranty)
	const [displayWarranty, setDisplayWarranty] = useState(false);

	// Default component
	let analysis = (
		<Box width='100%'>
			<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif' }}>
				Unit Analysis Loading...
			</Typography>
		</Box>
	);

	// If unitAnalysisData is provided and not empty, build the analysis
	if (unitAnalysisData !== undefined && !isEmpty(unitAnalysisData)) {
		analysis = unitAnalysisData.strings.map((string, stringIndex) => (
			<Grid item xs={12} key={stringIndex} >
				<SingleStringUnitsSummary
					string={string}
					stringIndex={stringIndex}
					displayAllAnalyses={displayAllAnalyses}
					displayAllUnits={displayAllUnits}
					displayWarranty={displayWarranty}
				/>
			</Grid>
		));
	}
	
	return (
		<Card style={{ border: '1px solid #D8D7D6', borderRadius: '5px', boxShadow: '2px 2px 4px gray', px: 0 }}>
			<Box sx={{ backgroundColor: 'lightgray', p: 1 }}>
				<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontWeight: 'bold' }}>
					Unit Analysis Summary
				</Typography>
			</Box>
			<CardContent style={{ padding: 10, position: 'relative' }}>
				{loading && (
					<Backdrop
						sx={{
							color: '#fff',
							zIndex: (theme) => theme.zIndex.drawer + 1,
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							borderRadius: 'inherit',
							backgroundColor: 'rgba(0,0,0,0.5)'
						}}
						open={true}
					>
						<CircularProgress color="inherit" />
					</Backdrop>
				)}
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 1 }}>
					<Tooltip title="Show All Analyses">
						<IconButton
							color={displayAllAnalyses ? "primary" : "default"}
							sx={{ border: '1px solid', borderColor: displayAllAnalyses ? 'primary.main' : 'grey.500', marginRight: 1 }}
							onClick={() => { setDisplayAllAnalyses(!displayAllAnalyses); }}
						>
							<InLimitsIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Show All Units">
						<IconButton
							color={displayAllUnits ? "primary" : "default"}
							disabled={displayAllAnalyses}  // Disable if showing all analyses
							sx={{ border: '1px solid', borderColor: displayAllUnits ? 'primary.main' : 'grey.500', marginRight: 1 }}
							onClick={() => { setDisplayAllUnits(!displayAllUnits); }}
						>
							<AllUnitsIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Show Warranty Info">
						<IconButton
							color={displayWarranty ? "primary" : "default"}
							sx={{ border: '1px solid', borderColor: displayWarranty ? 'primary.main' : 'grey.500', marginRight: 1 }}
							onClick={() => { setDisplayWarranty(!displayWarranty); }}
						>
							<WarrantyIcon />
						</IconButton>
					</Tooltip>
				</Box>
				<Grid container spacing={1} p={0}>
					{ analysis }
				</Grid>
			</CardContent>
		</Card>
	);
}

// Build the summary for a single string's worth of unit analyses
function SingleStringUnitsSummary({ string, stringIndex, displayAllAnalyses, displayAllUnits, displayWarranty }) {
	if (string && !isEmpty(string)) {
		
		const allUnitsWithinLimitsRow = (
			<TableRow>
				<TableCell colSpan={4} align="left" sx={{ backgroundColor: STATE_COLOR.WITHIN_LIMITS + 'aa', py: 0, border: 'none' }}>
					{ allUnitsWithinLimitsText }
				</TableCell>
			</TableRow>
		);

		// Collect all TableRow elements from all units (flattened)
		const stringNum = string.string_num;
		const summaries = string.units.flatMap(unit =>
			SingleUnitSummary({
				stringNum,
				unitData: unit,
				displayAllAnalyses,
				displayAllUnits,
				displayWarranty
			}));

		return (
			<Accordion key={stringIndex} defaultExpanded>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls={`panel${stringIndex}-content`}
					id={`panel${stringIndex}-header`}
				>
					<Typography sx={{ textAlign: 'left', fontFamily: 'Saira,sans-serif', fontSize: '14px', fontWeight: 'bold' }}>
						String {string.string_num}
					</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell align="center" width={unitNumColumnWidth} sx={{ py: 0, px: 1, border: 'none' }} >
										<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontSize: '12px', fontWeight: 'bold' }}>
											Unit
										</Typography>
									</TableCell>
									<TableCell align="center" width={installDateColumnWidth} sx={{ py: 0, px: 1, border: 'none' }} >
										<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontSize: '12px', fontWeight: 'bold' }}>
											Install Date
										</Typography>
									</TableCell>
									<TableCell align="center" width={measurementColumnWidth} sx={{ py: 0, px: 1, border: 'none' }} >
										<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontSize: '12px', fontWeight: 'bold' }}>
											Measurement
										</Typography>
									</TableCell>
									<TableCell align="left"  sx={{ py: 0, px: 1, border: 'none' }} >
										<Typography sx={{ textAlign: 'left', fontFamily: 'Saira,sans-serif', fontSize: '12px', fontWeight: 'bold' }}>
											Analysis
										</Typography>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{ (summaries && summaries.length > 0) ? summaries : allUnitsWithinLimitsRow }
							</TableBody>
						</Table>
					</TableContainer>
				</AccordionDetails>
			</Accordion>
		);
	}
	return null;
}

// Build the summary for a single unit and all of its various analyses
function SingleUnitSummary({ stringNum, unitData, displayAllAnalyses, displayAllUnits, displayWarranty }) {
	if (unitData && !isEmpty(unitData)) {
		let analysisTableRows = [];

		const unitNum = unitData.unit_num;
		const stats = unitData.stats;

		let analyses = [
			{ 'type': ANALYSIS_TYPE.VOLTAGE, 'analysis': unitData.voltage },
			{ 'type': ANALYSIS_TYPE.IMPEDANCE, 'analysis': unitData.impedance },
			{ 'type': ANALYSIS_TYPE.DIFF_TEMPERATURE, 'analysis': unitData.diff_temp },
			{ 'type': ANALYSIS_TYPE.TEMPERATURE, 'analysis': unitData.temperature },
		];

		// Check for valid discharge voltage analysis. If it's valid and below maintenance or critical limits, include in analyses
		if (unitData.discharge_voltage &&
			( unitData.discharge_voltage.analysis_result === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS ||
			  unitData.discharge_voltage.analysis_result === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL )
		) {
			const dischargeVoltageAnalysis = {
				'measurement': null,
				'analysis_result': unitData.discharge_voltage.analysis_result,
				'analysis_out': null,
				'analysis_valid': true,
				'out_date': unitData.discharge_voltage.out_date,
			};
			analyses.push({ 'type': ANALYSIS_TYPE.DISCHARGE_VOLTAGE, 'analysis': dischargeVoltageAnalysis });
		}

		// Filter out the analyses that are within limits if selected
		if (!displayAllAnalyses) {
			analyses = analyses.filter((analysis) => {
				const { type, analysis: a } = analysis;

				if (!a || a.analysis_result === null || a.analysis_result === undefined) return false;

				if (type === ANALYSIS_TYPE.VOLTAGE) {
					const { voltage } = parseVoltageAnalysisResult(a.analysis_result);
					return voltage !== ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS;
				}

				if (type === ANALYSIS_TYPE.IMPEDANCE) {
					const { impedance, openUnit } = parseImpedanceAnalysisResult(a.analysis_result);
					return (
						impedance !== ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS ||
						openUnit > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS
					);
				}

				// For other types, just check the top-level analysis_result
				return a.analysis_result !== ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS;
			});
		}

		// Sort the analyses by severity
		if (analyses && analyses.length > 1) {
			analyses = sortAnalysesBySeverity(analyses);
		}

		// Generate the typical analysis rows for this unit
		if (analyses && analyses.length > 0) {
			analysisTableRows = analyses.map((analysis, index) => (
				SingleAnalysisSummary('s' + stringNum + 'u' + unitNum + '.' + index, analysis.analysis, analysis.type, unitNum, stats.install_date, index === 0)
			));
		}
		
		// Display that the unit is within limits if displayAllUnits is selected and there are no analyses
		if (displayAllUnits && analyses && analyses.length === 0) {
			const withinLimitsColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS) + 'aa';
			const isFirstRow = analysisTableRows.length === 0;
			
			analysisTableRows.push(BuildAnalysisTableRow('s' + stringNum + 'u' + unitNum, isFirstRow, unitNum, stats.install_date, '', unitWithinLimitsText, withinLimitsColor));
		}
		
		// Check if the warranty information is selected to be shown - Not a part of the Analysis sorting/filtering
		if (displayWarranty) {
			const expiration = stats.warranty_expiration ? new Date(stats.warranty_expiration) : null;
			const isFirstRow = analysisTableRows.length === 0;

			let statusText = '';
			if (!expiration || expiration < new Date(1990, 0, 1)) {  // If no expiration date or invalid date
				statusText = 'This unit does not have valid warranty information available.';
			} else if (stats.out_of_warranty) {
				statusText = `This unit's warranty expired on ${formatDate(expiration)}.`;
			} else if (!stats.out_of_warranty) {
				statusText = `This unit is covered under warranty until ${formatDate(expiration)}.`;
			}

			if (stats.out_of_warranty || displayAllUnits || displayAllAnalyses) {  // Warranty expired or showing all units/analyses
				const keyText = 's' + stringNum + 'u' + unitNum + 'warranty';
				analysisTableRows.push(
					BuildAnalysisTableRow(keyText, isFirstRow, unitNum, stats.install_date, '', statusText, 'lightgray')
				);
			}
		}

		// Return array of TableRow elements (or empty array)
		return analysisTableRows;
	}
	return [];
}

// Sort the unit's analyses by severity; highest number up top
function sortAnalysesBySeverity(analyses) {
	if (!analyses || analyses.length <= 1) { return analyses; }

	// Function to get the severity of an analysis object - handles special cases with voltage and impedance
	function getSeverity(analysisObj) {
		const { type, analysis } = analysisObj;
		if (!analysis || analysis.analysis_result === null || analysis.analysis_result === undefined) return -Infinity;

		// Pull voltage analysis result out of the analysis_result object
		if (type === ANALYSIS_TYPE.VOLTAGE) {
			const { voltage } = parseVoltageAnalysisResult(analysis.analysis_result);
			return voltage;
		}

		// Pull impedance and openUnit analysis results out of the analysis_result object
		if (type === ANALYSIS_TYPE.IMPEDANCE) {
			const { impedance, openUnit } = parseImpedanceAnalysisResult(analysis.analysis_result);
			
			// If openUnit is above WITHIN_LIMITS but less than NO_ANALYSIS_DUE_TO_OPEN_STRING, give highest severity
			if (
				openUnit > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS &&
				openUnit < ANALYSIS_RESULT.NO_ANALYSIS_DUE_TO_OPEN_STRING
			) {
				return 10 + openUnit;
			}

			// If openUnit is NO_ANALYSIS_DUE_TO_OPEN_STRING, sort above WITHIN_LIMITS but below CRITICAL/MAINTENANCE
			if (openUnit === ANALYSIS_RESULT.NO_ANALYSIS_DUE_TO_OPEN_STRING) {
				return openUnit;
			}

			// If openUnit is WITHIN_LIMITS, use impedance severity
			return impedance;
		}

		// For all other types, just use the analysis_result as-is
		return analysis.analysis_result;
	}

	return analyses.slice().sort((a, b) => getSeverity(b) - getSeverity(a));
}

// Build the summary for a single analysis set
function SingleAnalysisSummary(key, analysis, analysisType, unitNum, installDate, isFirstRow) {
	if (analysis && !isEmpty(analysis)) {

		const isTempType = (analysisType === ANALYSIS_TYPE.TEMPERATURE || analysisType === ANALYSIS_TYPE.DIFF_TEMPERATURE);

		let measurement = buildMeasurementText(analysis.measurement, analysis.analysis_valid, analysis.analysis_out, analysis.analysis_result, analysisType);
		
		let color = AnalysisResultToStateColor(analysis.analysis_result) + 'aa';
		let measurementText = `${measurement.measText} ${measurement.outText}`;
		let stateText = '';
		
		if (analysisType === ANALYSIS_TYPE.VOLTAGE) {

			const { voltage, voltageSlope } = parseVoltageAnalysisResult(analysis.analysis_result);

			color = AnalysisResultToStateColor(voltage) + 'aa';
			stateText = UnitAnalysisToStateText(voltage, analysisType);

			// If voltage analysis and voltage slope analysis are out of limits, add "trending" text onto the analysis text
			if (voltage > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS && voltageSlope > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS) {
				stateText += ' and trending'
			}

		} else if (analysisType === ANALYSIS_TYPE.IMPEDANCE) {

			const { impedance, impedanceSlope, openUnit } = parseImpedanceAnalysisResult(analysis.analysis_result);

			// If the openUnit analysis is above "Within Limits", we need to indicate an issue with the string
			if (openUnit > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS) {

				color = AnalysisResultToStateColor(openUnit) + 'aa';
				measurementText = '-.--- mΩ';

				if (openUnit < ANALYSIS_RESULT.NO_ANALYSIS_DUE_TO_OPEN_STRING) {  // Open unit
					stateText = 'Unit has failed causing the entire string to become nonfunctional. Immediate Action required!';
				} else if (openUnit === ANALYSIS_RESULT.NO_ANALYSIS_DUE_TO_OPEN_STRING) {  // Open unit in the string, but not this one
					stateText = 'Unable to analyze impedance data due to the string being inoperable.';
					color = STATE_COLOR.UNKNOWN + 'aa';
				}
			} else {

				color = AnalysisResultToStateColor(impedance) + 'aa';
				stateText = UnitAnalysisToStateText(impedance, analysisType);
				
				// If impedance analysis and impedance slope analysis are out of limits, add "trending" text onto the analysis text
				if (impedance > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS && impedanceSlope > ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS) {
					stateText += ' and trending'
				}
			}
			
		} else if (isTempType) {

			stateText = UnitAnalysisToStateText(analysis.analysis_result, analysisType);

		} else if (analysisType === ANALYSIS_TYPE.DISCHARGE_VOLTAGE) {
			
			// Determine the discharge voltage state text based on the analysis result
			if (analysis.analysis_result === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS) {
				stateText = 'Unit voltage dropped down below maintenance limits during a recent discharge event';
			} else if (analysis.analysis_result === ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL) {
				stateText = 'Unit voltage dropped down below critical limits during a recent discharge event';
			} else {
				stateText = 'Unit voltage dropped down during a recent discharge event';
			}
		}	

		return (BuildAnalysisTableRow(key, isFirstRow, unitNum, installDate, measurementText, stateText, color));
	}
}

// Build a single analysis table row with consistent formatting and styling
function BuildAnalysisTableRow(key, isFirstRow, unitNum, installDate, measurementText, stateText, rowColor) {
	return (
		<TableRow key={key} sx={{ borderTop: isFirstRow ? '2px solid #FFFFFF' : 'none' }}>
			<TableCell align="left" width={unitNumColumnWidth} sx={{ backgroundColor: isFirstRow ? rowColor : 'inherit', py: 0, border: 'none' }} >
				{ isFirstRow ? unitNum : null }
			</TableCell>
			<TableCell align="center" width={installDateColumnWidth} sx={{ backgroundColor: isFirstRow ? rowColor : 'inherit', py: 0, border: 'none' }} >
				{ isFirstRow ? formatDate(installDate) : null }
			</TableCell>
			<TableCell align="left" width={measurementColumnWidth} sx={{ backgroundColor: rowColor, py: 0, px: 1, border: 'none' }} >
				{measurementText}
			</TableCell>
			<TableCell align="left" sx={{ backgroundColor: rowColor, py: 0, px: 1, border: 'none' }} >
				{stateText}
			</TableCell>
		</TableRow>
	);
}